import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import L from 'leaflet';
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { CalendarWidget } from './calender';
import { JournalRecord, normalizeJournalList } from '../journal/journalApi';
import { JournalSticker } from '../journal/journalStickers';
import { getJournalLayoutMap, JournalDeviceType, JournalLayoutInfo } from './scheduleApi';

export type WidgetSize = 'small' | 'medium' | 'large';

export interface WidgetLayoutData {
  id: string; // 고유 인스턴스 아이디 (드래그 시 식별용)
  type: string; // 'date', 'quote', 'dday'
  size: WidgetSize;
  // 홈 화면의 고정 격자 좌표. 예전 저장 데이터에는 없을 수 있어 optional로 둔다.
  page?: number;
  row?: number;
  col?: number;
}

const jiggle = keyframes`
  0%, 100% { transform: rotate(-0.35deg); }
  50% { transform: rotate(0.35deg); }
`;

const WidgetCard = styled(motion.div)<{ $size: WidgetSize; $isEditMode?: boolean; $isDragged?: boolean }>`
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(20px);
  border-radius: 28px;
  padding: ${p => p.$size === 'small' ? '20px' : '24px'};
  box-shadow: 0 10px 40px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  cursor: ${p => p.$isEditMode ? 'grab' : 'default'};
  opacity: ${p => p.$isDragged ? 0.4 : 1};
  
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  animation: ${p => p.$isEditMode ? jiggle : 'none'} 0.6s infinite ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #E05050;
  color: #fff;
  border: 2px solid #FDFBF4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  z-index: 20;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
`;

const ResizeBtn = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  box-shadow: 0 2px 10px rgba(0,0,0,0.15);
  color: #5C5040;
`;

const WidgetTitle = styled.div<{ $small?: boolean }>`
  font-size: ${p => p.$small ? '13px' : '15px'};
  font-weight: bold;
  margin-bottom: ${p => p.$small ? '8px' : '12px'};
  color: #333;
`;

const WidgetDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(0,0,0,0.1);
  margin: 8px 0;
`;

const WidgetContentArea = styled.div<{ $borderColor?: string; $small?: boolean }>`
  border-left: 3px solid ${p => p.$borderColor || '#65A3FF'};
  padding-left: ${p => p.$small ? '8px' : '12px'};
  font-size: ${p => p.$small ? '14px' : '16px'};
  color: #555;
  margin-top: ${p => p.$small ? '8px' : '12px'};
  display: flex;
  flex-direction: column;
  gap: ${p => p.$small ? '4px' : '8px'};
`;

export const WIDGET_DEFS = [
  { type: 'date', name: '오늘의 일정', defaultSize: 'medium' as WidgetSize },
  { type: 'quote', name: '오늘의 한마디', defaultSize: 'small' as WidgetSize },
  { type: 'dday', name: '디데이', defaultSize: 'small' as WidgetSize },
  { type: 'delivery-tracking', name: '실시간 배송', defaultSize: 'medium' as WidgetSize },
  { type: 'journal-write', name: '일기 쓰기', defaultSize: 'small' as WidgetSize },
  { type: 'journal-history', name: '내 일기 목록', defaultSize: 'medium' as WidgetSize }
];

interface DeliveryWidgetMail {
  id: string;
  counterpart: string;
  direction: 'sent' | 'received';
  sentAt: number;
  arrivalAt: number;
  transitRisk: string;
  clearanceRisk: string;
  deliveryRisk: string;
}

const parseMailDate = (value: any) => {
  if (!value) return 0;
  if (Array.isArray(value)) {
    return new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0).getTime();
  }
  let normalized = String(value).replace(' ', 'T');
  if (!normalized.endsWith('Z') && !normalized.includes('+')) normalized += '+09:00';
  return new Date(normalized).getTime();
};

const resolveMailArrival = (mail: any, sentAt: number, now: number) => {
  if (mail.estimated_arrival_at) return parseMailDate(mail.estimated_arrival_at);
  if (mail.remaining_minutes !== undefined && mail.remaining_minutes !== null && mail.remaining_minutes !== '') {
    return now + Number(mail.remaining_minutes) * 60000;
  }
  const receivedAt = mail.received_at ? parseMailDate(mail.received_at) : 0;
  if (receivedAt && Math.abs(receivedAt - sentAt) >= 60000) return receivedAt;
  const fallbackMinutes = Number(mail.arrival_delay_minutes) > 0
    ? Number(mail.arrival_delay_minutes)
    : (!isNaN(Number(mail.send_type)) && Number(mail.send_type) > 0 ? Number(mail.send_type) : 8640);
  return sentAt + fallbackMinutes * 60000;
};

const isNormalDeliveryRisk = (value: string) =>
  !value || !value.trim() || value.includes('정상 배송');

const formatRemainingDeliveryTime = (remainingMs: number) => {
  const totalMinutes = Math.max(0, Math.ceil(remainingMs / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}일 ${hours}시간`;
  if (hours > 0) return `${hours}시간 ${minutes}분`;
  return `${minutes}분`;
};

const DeliveryRouteScene = styled.div<{ $size: WidgetSize }>`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
  overflow: hidden;
  border: 1px solid rgba(79, 159, 196, 0.2);
  border-radius: ${p => p.$size === 'small' ? '15px' : '18px'};
  background-color: #DDF2F7;
  box-shadow: inset 0 0 32px rgba(255, 255, 255, 0.58);

  .leaflet-container {
    width: 100%;
    height: 100%;
    background: #DDF2F7;
    pointer-events: none;
    filter: saturate(0.72) brightness(1.08);
  }
`;

const DeliveryMapShade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.36), rgba(249,246,232,0.08) 48%, rgba(249,246,232,0.84));
  pointer-events: none;
`;

const DeliveryMapLabel = styled.span`
  position: absolute;
  z-index: 2;
  left: 50%;
  bottom: 8px;
  max-width: 76%;
  padding: 3px 8px;
  border: 1px solid rgba(126, 166, 177, 0.26);
  border-radius: 999px;
  background: rgba(255, 253, 245, 0.88);
  color: #665B50;
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transform: translateX(-50%);
  box-shadow: 0 2px 7px rgba(61, 48, 32, 0.08);
`;

const DeliveryRouteInfo = styled.div`
  position: absolute;
  z-index: 3;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
`;

const DeliveryStatusPill = styled.span`
  max-width: 42%;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(61, 48, 32, 0.78);
  color: #FFFDF8;
  font-size: 10px;
  font-weight: 750;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 3px 10px rgba(61, 48, 32, 0.14);
`;

const DeliveryTimePill = styled.span`
  min-width: 0;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(255, 253, 245, 0.9);
  color: #6D5E4B;
  font-size: 10px;
  font-weight: 750;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 3px 10px rgba(61, 48, 32, 0.09);
`;

const DeliveryProgressPill = styled.span`
  margin-left: auto;
  padding: 4px 7px;
  border-radius: 999px;
  background: rgba(61, 187, 176, 0.9);
  color: #FFF;
  font-size: 10px;
  font-weight: 800;
`;

type DeliveryLatLng = [number, number];

const getDeliveryDestination = (totalDays: number): DeliveryLatLng => {
  if (totalDays <= 1) return [37.566, 126.978];
  if (totalDays <= 2) return [35.676, 139.650];
  if (totalDays <= 4) return [15.000, 105.000];
  return [45.000, -100.000];
};

// 상세 배송 지도와 동일한 베지어 경로 계산을 사용합니다.
const getDeliveryCurve = (from: DeliveryLatLng, to: DeliveryLatLng, progress: number) => {
  const [lat1, lng1] = from;
  const [lat2, lng2] = to;
  let dx = lng2 - lng1;
  if (dx > 180) dx -= 360;
  else if (dx < -180) dx += 360;
  const dy = lat2 - lat1;
  const distance = Math.sqrt(dx * dx + dy * dy);
  let normalLat = -dx;
  let normalLng = dy;
  const normalLength = Math.sqrt(normalLat * normalLat + normalLng * normalLng);
  if (normalLength > 0) {
    normalLat /= normalLength;
    normalLng /= normalLength;
  }
  if (normalLat < 0) {
    normalLat = -normalLat;
    normalLng = -normalLng;
  }
  const curveHeight = distance * 0.15;
  const control: DeliveryLatLng = [
    (lat1 + lat2) / 2 + normalLat * curveHeight,
    (lng1 + lng2) / 2 + normalLng * curveHeight,
  ];
  const pointAt = (value: number): DeliveryLatLng => [
    (1 - value) * (1 - value) * lat1 + 2 * (1 - value) * value * control[0] + value * value * lat2,
    (1 - value) * (1 - value) * lng1 + 2 * (1 - value) * value * control[1] + value * value * lng2,
  ];
  return {
    points: Array.from({ length: 51 }, (_, index) => pointAt(index / 50)),
    currentPoint: pointAt(progress),
  };
};

const DeliveryMapCenter = ({ center }: { center: DeliveryLatLng }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);
  return null;
};

const DeliveryTrackingWidget = ({ size, onClick }: { size: WidgetSize; onClick?: (mailId?: string) => void }) => {
  const [mail, setMail] = useState<DeliveryWidgetMail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const isSmall = size === 'small';

  useEffect(() => {
    let isMounted = true;
    const fetchDelivery = async () => {
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const requestOptions: RequestInit = {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          cache: 'no-store',
        };
        const [sentResponse, receivedResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || ''}/mail/sent`, requestOptions).catch(() => null),
          fetch(`${process.env.REACT_APP_API_URL || ''}/mail/received`, requestOptions).catch(() => null),
        ]);
        if ((!sentResponse || !sentResponse.ok) && (!receivedResponse || !receivedResponse.ok)) {
          throw new Error('mail requests failed');
        }
        const [sentPayload, receivedPayload] = await Promise.all([
          sentResponse?.ok ? sentResponse.json() : Promise.resolve([]),
          receivedResponse?.ok ? receivedResponse.json() : Promise.resolve([]),
        ]);
        const getMailList = (payload: any) => {
          const list = Array.isArray(payload)
            ? payload
            : payload?.data ?? payload?.mails ?? payload?.items ?? [];
          return Array.isArray(list) ? list : [];
        };
        const candidates = [
          ...getMailList(sentPayload).map((item: any) => ({ item, direction: 'sent' as const })),
          ...getMailList(receivedPayload).map((item: any) => ({ item, direction: 'received' as const })),
        ];
        const requestedAt = Date.now();
        const activeMails = candidates
          .map(({ item, direction }): DeliveryWidgetMail | null => {
            const sentAt = parseMailDate(item.sent_at);
            const arrivalAt = resolveMailArrival(item, sentAt, requestedAt);
            const rawId = item.id ?? item.mail_id ?? item.mailId;
            if (rawId == null || !sentAt || !arrivalAt || arrivalAt <= requestedAt) return null;
            return {
              id: String(rawId),
              counterpart: direction === 'sent'
                ? item.receiver?.user_alias || item.receiver?.user_name || item.receiver_email || item.receiver_alias || '받는 사람'
                : item.user?.user_alias || item.user?.user_name || item.sender_email || item.sender_alias || '보낸 사람',
              direction,
              sentAt,
              arrivalAt,
              transitRisk: String(item.transit_risk || item.transitRisk || ''),
              clearanceRisk: String(item.clearance_risk || item.clearanceRisk || ''),
              deliveryRisk: String(item.delivery_risk || item.deliveryRisk || ''),
            };
          })
          .filter((item: DeliveryWidgetMail | null): item is DeliveryWidgetMail => item !== null)
          .sort((a: DeliveryWidgetMail, b: DeliveryWidgetMail) => b.sentAt - a.sentAt);
        let selectedMail = activeMails[0] ?? null;
        if (selectedMail) {
          try {
            const detailResponse = await fetch(
              `${process.env.REACT_APP_API_URL || ''}/mail/${selectedMail.id}`,
              requestOptions,
            );
            if (detailResponse.ok) {
              const detail = await detailResponse.json();
              const detailSentAt = parseMailDate(detail.sent_at);
              const detailArrivalAt = resolveMailArrival(detail, detailSentAt, requestedAt);
              if (detailSentAt && detailArrivalAt > requestedAt) {
                selectedMail = {
                  ...selectedMail,
                  counterpart: selectedMail.direction === 'sent'
                    ? detail.receiver?.user_alias || detail.receiver?.user_name || detail.receiver_email || detail.receiver_alias || selectedMail.counterpart
                    : detail.user?.user_alias || detail.user?.user_name || detail.sender_email || detail.sender_alias || selectedMail.counterpart,
                  sentAt: detailSentAt,
                  arrivalAt: detailArrivalAt,
                  transitRisk: String(detail.transit_risk || detail.transitRisk || ''),
                  clearanceRisk: String(detail.clearance_risk || detail.clearanceRisk || ''),
                  deliveryRisk: String(detail.delivery_risk || detail.deliveryRisk || ''),
                };
              }
            }
          } catch {
            // 상세 요청이 잠시 실패하면 목록에서 받은 최신 값으로 계속 표시합니다.
          }
        }
        if (isMounted) {
          setMail(selectedMail);
          setNow(requestedAt);
          setLoadFailed(false);
        }
      } catch {
        if (isMounted) setLoadFailed(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDelivery();
    const pollId = window.setInterval(fetchDelivery, 15000);
    const clockId = window.setInterval(() => setNow(Date.now()), 1000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchDelivery();
    };
    window.addEventListener('focus', fetchDelivery);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isMounted = false;
      window.clearInterval(pollId);
      window.clearInterval(clockId);
      window.removeEventListener('focus', fetchDelivery);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const progress = mail && mail.arrivalAt > mail.sentAt
    ? Math.min(100, Math.max(0, ((now - mail.sentAt) / (mail.arrivalAt - mail.sentAt)) * 100))
    : 0;
  let status = '준비 중';
  if (mail) {
    if (progress < 33.3) status = isNormalDeliveryRisk(mail.transitRisk) ? '준비 중' : mail.transitRisk;
    else if (progress < 66.6) status = isNormalDeliveryRisk(mail.clearanceRisk) ? '통관 중' : mail.clearanceRisk;
    else status = isNormalDeliveryRisk(mail.deliveryRisk) ? '배송 중' : mail.deliveryRisk;
  }
  const routeProgress = progress / 100;
  const totalDeliveryDays = mail ? (mail.arrivalAt - mail.sentAt) / 86400000 : 0;
  const routeOrigin = useMemo<DeliveryLatLng>(() => [37.566, 126.978], []);
  const routeDestination = useMemo(
    () => getDeliveryDestination(totalDeliveryDays),
    [totalDeliveryDays],
  );
  const routeData = useMemo(
    () => getDeliveryCurve(routeOrigin, routeDestination, routeProgress),
    [routeDestination, routeOrigin, routeProgress],
  );
  const mascotFacesRight = routeDestination[1] >= routeOrigin[1];
  const mascotIcon = useMemo(() => L.divIcon({
    className: '',
    html: `<img src="${process.env.PUBLIC_URL}/logo_image/fly1.png" alt="" style="width:110px;height:110px;object-fit:contain;transform:scaleX(${mascotFacesRight ? 1 : -1});filter:drop-shadow(0 5px 8px rgba(61,48,32,.2));" />`,
    iconSize: [110, 110],
    iconAnchor: [55, 55],
  }), [mascotFacesRight]);
  const routeStartLabel = mail?.direction === 'received' ? mail.counterpart : '나';
  const routeEndLabel = mail?.direction === 'received' ? '나' : mail?.counterpart;

  return (
    <div
      style={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: mail ? 'pointer' : 'default' }}
      onClick={() => mail && onClick?.(mail.id)}
    >
      <WidgetContentArea $small={isSmall} style={{ flex: 1, minHeight: 0, marginTop: 0, paddingLeft: 0, borderLeft: 'none', justifyContent: 'center', gap: 0 }}>
        {loading ? (
          <span style={{ color: '#999', fontSize: '12px' }}>배송 정보를 불러오는 중...</span>
        ) : loadFailed ? (
          <span style={{ color: '#999', fontSize: '12px' }}>배송 정보를 불러오지 못했어요.</span>
        ) : !mail ? (
          <span style={{ color: '#999', fontSize: '12px' }}>현재 이동 중인 편지가 없어요.</span>
        ) : (
          <>
            <DeliveryRouteScene $size={size}>
              <MapContainer
                center={routeData.currentPoint}
                zoom={4}
                zoomControl={false}
                attributionControl={false}
                dragging={false}
                scrollWheelZoom={false}
                doubleClickZoom={false}
                boxZoom={false}
                keyboard={false}
              >
                <DeliveryMapCenter center={routeData.currentPoint} />
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors &copy; CARTO"
                  url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                  subdomains={['a', 'b', 'c', 'd']}
                />
                <Polyline
                  positions={routeData.points}
                  pathOptions={{ color: '#4F9FC4', weight: 4, dashArray: '8 8', opacity: 0.95, lineCap: 'round' }}
                />
                <CircleMarker
                  center={routeOrigin}
                  radius={7}
                  interactive={false}
                  pathOptions={{
                    color: '#FFFDF5',
                    weight: 3,
                    fillColor: '#E07040',
                    fillOpacity: 1,
                  }}
                />
                <CircleMarker
                  center={routeDestination}
                  radius={7}
                  interactive={false}
                  pathOptions={{
                    color: '#FFFDF5',
                    weight: 3,
                    fillColor: '#3DBBB0',
                    fillOpacity: 1,
                  }}
                />
                <Marker position={routeData.currentPoint} icon={mascotIcon} zIndexOffset={1000} />
              </MapContainer>
              <DeliveryMapShade />
              <DeliveryRouteInfo>
                <DeliveryStatusPill>{status}</DeliveryStatusPill>
                <DeliveryTimePill>{formatRemainingDeliveryTime(mail.arrivalAt - now)} 남음</DeliveryTimePill>
                <DeliveryProgressPill>{Math.round(progress)}%</DeliveryProgressPill>
              </DeliveryRouteInfo>
              <DeliveryMapLabel>{routeStartLabel} → {routeEndLabel}</DeliveryMapLabel>
            </DeliveryRouteScene>
          </>
        )}
      </WidgetContentArea>
    </div>
  );
};

const JournalWriteWidget = ({ size, onClick }: { size: WidgetSize, onClick?: () => void }) => {
  const isSmall = size === 'small';
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => onClick?.()}>
      <WidgetTitle $small={isSmall}>일기 쓰기</WidgetTitle>
      {!isSmall && <WidgetDivider />}
      <WidgetContentArea $borderColor="#D06060" $small={isSmall} style={{ flex: 1, justifyContent: 'center', alignItems: 'center', borderLeft: 'none', paddingLeft: 0, marginTop: 0 }}>
        <div style={{ fontSize: isSmall ? '28px' : '40px', marginBottom: '8px' }}>✏️</div>
        <div style={{ fontSize: isSmall ? '13px' : '15px', fontWeight: 'bold', color: '#D06060' }}>오늘 하루 기록하기</div>
      </WidgetContentArea>
    </div>
  );
};

const JournalDeviceIcon: React.FC<{ device: JournalDeviceType; size: number }> = ({ device, size }) => device === 'pc' ? (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
) : (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="7" y="2" width="10" height="20" rx="2.5" />
    <path d="M11 18h2" />
  </svg>
);

const JournalHistoryWidget = ({ size, onClick }: { size: WidgetSize, onClick?: () => void }) => {
  const [journals, setJournals] = useState<JournalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    const fetchJournals = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/journal`, {
           headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || ''}` }
        });
        if (!res.ok) throw new Error(`journal request failed: ${res.status}`);
        const data = await res.json();
        const normalized = normalizeJournalList(data);
        const layoutMap = await getJournalLayoutMap(normalized.map(journal => journal.journal_id))
          .catch(() => ({} as Record<string, JournalLayoutInfo>));
        const list = normalized
          .map(journal => ({
            ...journal,
            emoticon: layoutMap[journal.journal_id]?.sticker || journal.emoticon,
            authoredDevice: layoutMap[journal.journal_id]?.device,
            authoredCanvasWidth: layoutMap[journal.journal_id]?.canvasWidth,
          }))
          .sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        if (isMounted) setJournals(list);
      } catch (e) {
        if (isMounted) setLoadFailed(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchJournals();
    return () => { isMounted = false; };
  }, []);

  const isSmall = size === 'small';
  const visibleCount = size === 'large' ? 6 : size === 'medium' ? 5 : 3;
  const visibleJournals = journals.slice(0, visibleCount);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => onClick?.()}>
      <WidgetTitle $small={isSmall} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>내 일기</span>
        {!loading && journals.length > 0 && (
          <span style={{ color: '#A09070', fontSize: '11px', fontWeight: 500 }}>{journals.length}개</span>
        )}
      </WidgetTitle>
      {!isSmall && <WidgetDivider />}
      <WidgetContentArea $borderColor="#9060D0" $small={isSmall} style={{ flex: 1, overflow: 'hidden', gap: 0 }}>
        {loading ? (
          <div style={{ color: '#999', fontSize: '13px', margin: 'auto 0' }}>불러오는 중...</div>
        ) : loadFailed ? (
          <div style={{ color: '#999', fontSize: '13px', margin: 'auto 0' }}>일기를 불러오지 못했어요.</div>
        ) : visibleJournals.length > 0 ? (
          visibleJournals.map((journal, index) => (
            <div
              key={journal.journal_id}
              onClick={(event) => {
                event.stopPropagation();
                onClick?.();
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                alignItems: 'center',
                gap: '8px',
                minHeight: isSmall ? '38px' : '42px',
                borderBottom: index < visibleJournals.length - 1 ? '1px solid rgba(0,0,0,0.07)' : 'none',
              }}
            >
              <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '7px' }}>
                <span aria-label="그날의 기분" style={{ flex: '0 0 auto', lineHeight: 1 }}>
                  <JournalSticker value={journal.emoticon} size={isSmall ? 24 : 28} />
                </span>
                <span style={{ display: 'block', color: '#3D3020', fontSize: isSmall ? '12px' : '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {journal.title?.trim() || '제목 없는 일기'}
                </span>
              </span>
              <span style={{ color: '#A09070', fontSize: '10px', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                <span>{new Date(journal.created_at).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</span>
                {journal.authoredDevice && (
                  <span
                    aria-label={journal.authoredDevice === 'pc' ? 'PC에서 작성' : '모바일에서 작성'}
                    title={journal.authoredDevice === 'pc' ? 'PC에서 작성' : '모바일에서 작성'}
                    style={{ display: 'flex', color: '#E07040' }}
                  >
                    <JournalDeviceIcon device={journal.authoredDevice} size={13} />
                  </span>
                )}
              </span>
            </div>
          ))
        ) : (
          <div style={{ color: '#999', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
            아직 작성한 일기가 없어요.<br/>첫 일기를 남겨보세요.
          </div>
        )}
      </WidgetContentArea>
    </div>
  );
};

interface WidgetContainerProps {
  data: WidgetLayoutData;
  isEditMode?: boolean;
  isDragged?: boolean;
  onRemove?: (id: string) => void;
  onResize?: (id: string) => void;
  onWidgetClick?: (type: string, itemId?: string) => void;
}

export const WidgetContainer: React.FC<WidgetContainerProps> = ({ 
  data, isEditMode, isDragged, onRemove, onResize, onWidgetClick
}) => {
  const isSmall = data.size === 'small';
  const stopEditControlPointer = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  const renderContent = () => {
    switch (data.type) {
      case 'date':
        return <CalendarWidget size={data.size} onClick={() => !isEditMode && onWidgetClick?.('date')} />;
      case 'quote':
        return (
          <>
            <WidgetTitle $small={isSmall}>한마디</WidgetTitle>
            {!isSmall && <WidgetDivider />}
            <WidgetContentArea $borderColor="#60C080" $small={isSmall}>
              <span style={{ fontStyle: 'italic', fontSize: isSmall ? '14px' : '17px', fontWeight: '500', color: '#3D3020' }}>
                "시작이 반이다."
              </span>
              <span style={{ fontSize: '12px', color: '#888' }}>- 아리스토텔레스</span>
            </WidgetContentArea>
          </>
        );
      case 'dday':
        return (
          <>
            <WidgetTitle $small={isSmall}>디데이</WidgetTitle>
            {!isSmall && <WidgetDivider />}
            <WidgetContentArea $borderColor="#E07040" $small={isSmall}>
              <span style={{ fontSize: isSmall ? '20px' : '24px', fontWeight: 'bold', color: '#E07040' }}>D - 12</span>
              <span style={{ fontSize: '12px' }}>우체통 오픈까지</span>
            </WidgetContentArea>
          </>
        );
      case 'delivery-tracking':
        return <DeliveryTrackingWidget size={data.size} onClick={(mailId) => !isEditMode && onWidgetClick?.('delivery-tracking', mailId)} />;
      case 'journal-write':
        return <JournalWriteWidget size={data.size} onClick={() => !isEditMode && onWidgetClick?.('journal-write')} />;
      case 'journal-history':
        return <JournalHistoryWidget size={data.size} onClick={() => !isEditMode && onWidgetClick?.('journal-history')} />;
      default:
        return <div style={{color: '#999'}}>알 수 없는 위젯</div>;
    }
  };

  return (
    <WidgetCard 
      $size={data.size} 
      $isEditMode={isEditMode}
      $isDragged={isDragged}
    >
      {isEditMode && (
        <>
          <RemoveBtn
            type="button"
            aria-label="위젯 삭제"
            onPointerDown={stopEditControlPointer}
            onPointerMove={stopEditControlPointer}
            onPointerUp={stopEditControlPointer}
            onPointerCancel={stopEditControlPointer}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove?.(data.id); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </RemoveBtn>
          <ResizeBtn
            type="button"
            aria-label="위젯 크기 변경"
            onPointerDown={stopEditControlPointer}
            onPointerMove={stopEditControlPointer}
            onPointerUp={stopEditControlPointer}
            onPointerCancel={stopEditControlPointer}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onResize?.(data.id); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </ResizeBtn>
        </>
      )}

      <motion.div
        key={data.type + data.size}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        style={{ height: '100%', flex: 1 }}
      >
        {renderContent()}
      </motion.div>
    </WidgetCard>
  );
};

/* Preview Component used in Widget Gallery Modal */
export const WidgetPreview: React.FC<{ type: string }> = ({ type }) => {
  const mockData: WidgetLayoutData = { id: 'preview', type, size: 'small' };
  return (
    <div style={{ width: '112px', height: '112px' }}>
      <div style={{ transform: 'scale(0.7)', transformOrigin: 'top left', width: '160px', height: '160px' }}>
        <WidgetContainer data={mockData} />
      </div>
    </div>
  );
};
